<script setup lang="ts">
import type { HTMLAttributes } from 'vue';
import type { FormFieldErrors as Errors } from './internal/context';
import type { FieldControlState, ResolvedControlWidth } from '@/components/base/_internal/control';
import { computed, ref, useId, useSlots } from 'vue';
import { injectFieldControl, provideControlDefaults, provideFieldControl, useControlSize } from '@/components/base/_internal/control';
import { SphLabel } from '@/components/base/label';
import { cn } from '@/utils/shadcn/lib/utils';
import { injectFormRow, normalizeErrors, provideFormRow } from './internal/context';
import FormFieldErrors from './internal/FormFieldErrors.vue';

defineOptions({
    compatConfig: {
        MODE: 3,
    },
});

const props = withDefaults(defineProps<{
    label?: string
    description?: string
    errors?: Errors
    /**
     * `stack` puts the label above the control, `side` beside it (and above again in a narrow
     * container), `choice` after a checkbox, switch or radio.
     */
    layout?: 'stack' | 'side' | 'choice'
    /** The label column in the `side` layout. `auto` fits the label and never stacks. */
    labelWidth?: string
    /** The control's id. Generated when omitted. */
    id?: string
    required?: boolean
    invalid?: boolean
    disabled?: boolean
    class?: HTMLAttributes['class']
}>(), {
    label: '',
    description: '',
    errors: null,
    layout: 'stack',
    labelWidth: undefined,
    id: undefined,
    required: false,
    invalid: false,
    disabled: false,
    class: '',
});

const slots = useSlots();

const baseId = useId();
const controlId = computed(() => props.id || `${baseId}-control`);
const descriptionId = `${baseId}-description`;
const errorsId = `${baseId}-errors`;

const errorList = computed(() => normalizeErrors(props.errors));
const hasLabel = computed(() => !!(props.label || slots.label));
const hasDescription = computed(() => !!(props.description || slots.description));

const parent = injectFieldControl();
const inRow = injectFormRow();
const size = useControlSize(() => undefined, 'md');

const state = computed<FieldControlState>(() => ({
    id: controlId.value,
    describedBy: [
        hasDescription.value && descriptionId,
        errorList.value.length > 0 && errorsId,
    ].filter(Boolean).join(' ') || undefined,
    invalid: props.invalid || errorList.value.length > 0,
    disabled: props.disabled || !!parent?.state.value.disabled,
    required: props.required,
}));

// Anything that is not a control reports nothing, and is treated as filling the field.
const controlWidth = ref<ResolvedControlWidth>('full');

provideFieldControl({
    state,
    reportWidth: (width) => {
        controlWidth.value = width;
    },
});
provideControlDefaults({ legacyWidth: false });
provideFormRow(false);

const labelWidth = computed(() => (props.labelWidth === 'auto' ? 'auto' : 'fixed'));
</script>

<template>
    <div
        data-slot="form-field"
        :data-layout="props.layout"
        :data-label-width="props.layout === 'side' ? labelWidth : undefined"
        :data-control-width="controlWidth"
        :data-size="size"
        :data-in-row="inRow || undefined"
        :data-invalid="state.invalid || undefined"
        :data-disabled="state.disabled || undefined"
        :class="cn('sph-form-field group', props.class)"
        :style="props.labelWidth && props.labelWidth !== 'auto' ? { '--form-field-label-width': props.labelWidth } : undefined"
    >
        <div class="sph-form-field__grid">
            <SphLabel
                v-if="hasLabel"
                :for="controlId"
                :inline="props.layout === 'choice'"
                data-slot="form-field-label"
                class="sph-form-field__label"
            >
                <span>
                    <slot name="label">{{ props.label }}</slot><span
                        v-if="props.required"
                        aria-hidden="true"
                    >*</span>
                </span>
            </SphLabel>

            <p
                v-if="hasDescription"
                :id="descriptionId"
                data-slot="form-field-description"
                class="sph-form-field__description"
            >
                <slot name="description">
                    {{ props.description }}
                </slot>
            </p>

            <div
                data-slot="form-field-control"
                class="sph-form-field__control"
            >
                <slot v-bind="state"></slot>
            </div>

            <FormFieldErrors
                v-if="errorList.length > 0"
                :id="errorsId"
                :errors="errorList"
                class="sph-form-field__errors"
            />
        </div>
    </div>
</template>

<style scoped>
@reference '@/assets/styles/config.css';

/*
 * One grid, five rows, for every layout:
 *   1 the label above · 2 the description above · 3 the control line · 4 the description below · 5 errors
 * A layout only moves the parts between rows and columns. An empty row takes no height, and the
 * spacing lives in margins rather than gaps, so a missing part leaves no gap behind — and a row of
 * fields can share the five rows through subgrid.
 */
.sph-form-field {
    --form-field-line: var(--input-height);

    min-width: 0;
}

.sph-form-field[data-size='sm'] {
    --form-field-line: var(--input-height-small);
}

.sph-form-field[data-size='lg'] {
    --form-field-line: var(--button-height-lg);
}

.sph-form-field__grid {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: repeat(5, auto);
    column-gap: calc(var(--spacing) * 3);
}

.sph-form-field__label {
    grid-area: 1 / 1;
    align-self: end;
    margin-bottom: calc(var(--spacing) * 2);
}

.sph-form-field__description {
    grid-area: 2 / 1;
    margin-bottom: calc(var(--spacing) * 2);

    @apply text-sm leading-normal text-muted-foreground [&_a]:underline [&_a]:underline-offset-4 [&_a:hover]:text-primary;
}

.sph-form-field__control {
    grid-area: 3 / 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    min-width: 0;
}

.sph-form-field__errors {
    grid-area: 5 / 1;
    margin-top: calc(var(--spacing) * 1.5);
}

/* Help and errors wrap inside the width the label and control give the field; they never widen it. */
.sph-form-field__description,
.sph-form-field__errors {
    contain: inline-size;
}

/* side: the label sits in its own column, centred on the control's first line */
.sph-form-field[data-layout='side'] {
    > .sph-form-field__grid {
        grid-template-columns: var(--form-field-label-width) minmax(0, 1fr);
        column-gap: var(--field-gap-x);
    }

    .sph-form-field__label {
        grid-area: 3 / 1;
        align-self: start;
        min-height: var(--form-field-line);
        margin-bottom: 0;
    }

    .sph-form-field__control {
        grid-area: 3 / 2;
        min-height: var(--form-field-line);
    }

    .sph-form-field__description {
        grid-area: 4 / 2;
        margin: calc(var(--spacing) * 1.5) 0 0;
    }

    .sph-form-field__errors {
        grid-area: 5 / 2;
    }
}

.sph-form-field[data-layout='side'][data-label-width='auto'] > .sph-form-field__grid {
    grid-template-columns: auto minmax(0, 1fr);
    column-gap: calc(var(--spacing) * 2.5);
}

/* A fixed label column needs room next to it; below that the field stacks. A fitted label never does. */
.sph-form-field[data-layout='side'][data-label-width='fixed'] {
    container: sph-form-field / inline-size;
}

@container sph-form-field (width < 30rem) {
    .sph-form-field[data-layout='side'] {
        > .sph-form-field__grid {
            grid-template-columns: minmax(0, 1fr);
        }

        .sph-form-field__label {
            grid-area: 1 / 1;
            min-height: 0;
            margin-bottom: calc(var(--spacing) * 2);
        }

        .sph-form-field__description {
            grid-area: 2 / 1;
            margin: 0 0 calc(var(--spacing) * 2);
        }

        .sph-form-field__control {
            grid-area: 3 / 1;
            min-height: 0;
        }

        .sph-form-field__errors {
            grid-area: 5 / 1;
        }
    }
}

/* choice: the control first, the label and everything else in the column after it */
.sph-form-field[data-layout='choice'] {
    --form-field-line: calc(var(--spacing) * 6);

    > .sph-form-field__grid {
        grid-template-columns: auto minmax(0, 1fr);
    }

    .sph-form-field__control {
        grid-area: 3 / 1;
        align-self: start;
        min-height: var(--form-field-line);
    }

    .sph-form-field__label {
        grid-area: 3 / 2;
        align-self: start;
        min-height: var(--form-field-line);
        margin-bottom: 0;
    }

    .sph-form-field__description {
        grid-area: 4 / 2;
        margin: calc(var(--spacing) * 0.5) 0 0;
    }

    .sph-form-field__errors {
        grid-area: 5 / 2;
    }
}

/* In a row the field hands its five rows to the row, so labels, controls and errors line up across fields. */
.sph-form-field[data-in-row] {
    display: grid;
    grid-row: span 5;
    grid-template-rows: subgrid;
    grid-template-columns: minmax(0, 1fr);

    > .sph-form-field__grid {
        grid-row: span 5;
        grid-template-rows: subgrid;
    }
}
</style>
